import type { Money, ServiceDefinition, ServiceSeed } from '@/types';
import { SERVICE_SEED_SETS } from './seeds';
import { WORKBOOK_DEFAULT_PRICES, WORKBOOK_PRICING_NOTES } from './workbookPrices.generated';

/**
 * Resolves each authored service's system default price.
 *
 * Price precedence, lowest to highest. Each layer is copied from the one below
 * it once and never writes back:
 *
 *   1. System default  `ServiceDefinition.basePrice` — the pricing workbook
 *      (`WORKBOOK_DEFAULT_PRICES`, generated by `npm run import:pricing`), or the
 *      template file's own `basePrice` for a service the workbook could not
 *      price. Exactly one of the two, never both and never neither.
 *   2. Owner price     `Service.price` — copied from (1) when the business is
 *      created (`seedCatalog`), then repriced freely on the Services page.
 *   3. Quote line      `QuoteItem.unitPrice` — copied from (2) when a service is
 *      added to a quote, editable per line. A saved quote keeps its own copy, so
 *      changing (1) or (2) never rewrites an existing quote.
 *
 * The unit travels with the price at every layer and is authored in the
 * template file; the import refuses to integrate a workbook price in a unit the
 * catalog does not sell the service in. A default the workbook only states
 * together with a pricing model also carries that model as `pricingNote` —
 * generated beside the price, never without it.
 */
export function resolveDefaultPrices(
  seeds: readonly ServiceSeed[],
  workbookPrices: Readonly<Record<string, Money>> = WORKBOOK_DEFAULT_PRICES,
  pricingNotes: Readonly<Record<string, string>> = WORKBOOK_PRICING_NOTES,
): ServiceDefinition[] {
  return seeds.map((seed) => {
    const workbookPrice = Object.hasOwn(workbookPrices, seed.id) ? workbookPrices[seed.id] : undefined;
    const pricingNote = Object.hasOwn(pricingNotes, seed.id) ? pricingNotes[seed.id] : undefined;

    if (workbookPrice !== undefined && seed.basePrice !== undefined) {
      throw new Error(`[templates] "${seed.id}" is priced by the pricing workbook and also authors a basePrice.`);
    }
    if (pricingNote !== undefined && workbookPrice === undefined) {
      throw new Error(`[templates] "${seed.id}" carries a workbook pricing note but no workbook price.`);
    }
    const basePrice = workbookPrice ?? seed.basePrice;
    if (basePrice === undefined) {
      throw new Error(`[templates] "${seed.id}" has no default price: the pricing workbook does not price it and it authors no basePrice.`);
    }
    return pricingNote === undefined ? { ...seed, basePrice } : { ...seed, basePrice, pricingNote };
  });
}

export const SERVICE_SETS: Readonly<Record<string, readonly ServiceDefinition[]>> = Object.fromEntries(
  Object.entries(SERVICE_SEED_SETS).map(([templateId, seeds]) => [templateId, resolveDefaultPrices(seeds)]),
);
